"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { recordDebugEvent } from "@/lib/debug";
import { CACHE_NAMES, TRIPS_URL } from "@/lib/cache-constants";
import { loginUrl, readCachedJson, writeCachedJson } from "@/lib/pwa-refresh";
import { formatDateRange } from "@/lib/format";
import { Badge, FullPageSpinner, OfflineBanner, cardStyle } from "@/components/trip/ui";
import type { Trip } from "@/lib/types";

const DATA_CACHE = CACHE_NAMES.tripData;

export default function TripsPage() {
  const { status } = useSession();
  const router = useRouter();
  const isOnline = useOnlineStatus();
  const [trips, setTrips] = useState<Trip[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Cache-first, network ONLY on a cold start (empty cache) — matching
      // the manual-only-refresh model of the rest of the app. The list is
      // NOT re-validated on every mount: a trip's Sync Now already re-caches
      // the trips list here when its manifest hash changes (see
      // trip-data-context), so navigating to /trips is network-silent once
      // the list has been cached. (This used to background-revalidate on
      // every visit — a ~1 KB network call per navigation.)
      const t0 = performance.now();
      const cached = await readCachedJson<Trip[]>(DATA_CACHE, TRIPS_URL);
      if (cached) {
        if (cancelled) return;
        setTrips(cached);
        setLoading(false);
        recordDebugEvent({
          kind: "trips-list",
          url: TRIPS_URL,
          source: `cache:${DATA_CACHE}`,
          durationMs: Math.round(performance.now() - t0),
          detail: "app cache-first",
        });
        return;
      }

      // Cold start: no cached list yet — fetch once and cache it.
      try {
        const res = await fetch(TRIPS_URL);
        if (!res.ok) throw new Error("Failed to load trips");
        const payload: Trip[] = await res.json();
        if (cancelled) return;
        recordDebugEvent({
          kind: "trips-list",
          url: TRIPS_URL,
          source: `network→cache:${DATA_CACHE}`,
          durationMs: Math.round(performance.now() - t0),
          status: res.status,
          detail: "cold start",
        });
        await writeCachedJson(DATA_CACHE, TRIPS_URL, payload);
        setTrips(payload);
      } catch {
        /* offline with no cached list — the redirect/empty state handles it */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Redirect to login only when online, confirmed unauthenticated, and the
  // load has settled with no cached data (never mid-load — see TripChrome).
  useEffect(() => {
    if (status === "unauthenticated" && isOnline && !trips && !loading) {
      router.push(loginUrl("/trips"));
    }
  }, [status, isOnline, trips, loading, router]);

  if (loading || !trips) {
    return <FullPageSpinner label="Loading trips..." />;
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px 80px" }}>
      {!isOnline && <OfflineBanner />}

      {/* App header — matches the per-trip and Finances chrome. */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 14,
          marginBottom: 22,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
            🧳
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.01em" }}>
            Vacations
          </span>
        </span>
        <Link
          href="/"
          aria-label="All apps"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            borderRadius: 999,
            color: "var(--text3)",
            textDecoration: "none",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 12l9-8 9 8M5 10v10h5v-6h4v6h5V10" />
          </svg>
        </Link>
      </header>

      <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
        Trips
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, margin: "0 0 36px" }}>
        All trips
      </h1>

      {(trips ?? []).map((trip, i) => (
          <Link
            key={trip.id}
            href={`/trip/${trip.id}`}
            style={{ ...cardStyle, display: "block", marginBottom: 14, textDecoration: "none", color: "inherit" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{trip.title}</div>
              {i === 0 && <Badge>Latest</Badge>}
            </div>
            <div style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text3)", marginBottom: 6 }}>
              {formatDateRange(trip.dates.start, trip.dates.end)} · {trip.travelers.join(" & ")}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-warm)", lineHeight: 1.5 }}>
              {trip.routeLine}
            </div>
          </Link>
        ))}
    </div>
  );
}
